(ns hai.entity.kebab
  "Shared ⋮ actions menu for entity cards — notes and tasks alike: ordinary
   actions plus an optional destructive Delete that swaps the menu to an inline
   confirm before firing."
  (:require
   [clojure.string :as str]
   [hai.entity.sections :as sections]
   [hai.ui.components :as c]
   [hai.ui.components.dropdown-menu :as dropdown-menu]
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private trash-icon (icons/mui-icon :outlined :delete))

(def ^:private app-base "https://my.humbleai.com/app")

(defn app-url
  "The my.humbleai.com URL of the web-app page at `segments`, e.g.
   `(app-url \"notes\" note-uuid)`. Always the production host, so a URL
   copied from any surface — the extension included — opens the same page."
  [& segments]
  (str/join "/" (cons app-base segments)))

(defn- copy-url-item [url]
  ["Copy URL"
   (fn [_]
     (-> js/navigator
         .-clipboard
         (.writeText url)
         (.then #(>e [:toast/success {:text "URL copied to clipboard"}]))
         (.catch js/console.error)))
   icons/outlined-content_copy])

(o/defstyled confirm-title :div
  {:font-size "12.5px" :font-weight "600" :color t/--note-kraft :padding "8px 10px 4px"})

(o/defstyled confirm-row :div
  {:display "flex" :gap "8px" :padding "0 8px 6px"})

(o/defstyled confirm-cancel :button
  {:flex "1" :border "1px solid rgba(50,64,37,0.25)" :border-radius "7px"
   :background "transparent" :color t/--note-kraft :font-family "inherit"
   :font-weight "600" :font-size "12.5px" :padding "6px 10px" :cursor "pointer"})

(o/defstyled confirm-delete :button
  {:flex "1" :border "none" :border-radius "7px"
   :background t/--card-error :color "white" :font-family "inherit"
   :font-weight "600" :font-size "12.5px" :padding "6px 10px" :cursor "pointer"}
  [:&:hover {:background "#7D2020"}])

;; A link item is the menu item itself (Radix `asChild`), so it takes the item's
;; look — minus the anchor's underline.
(o/defstyled menu-link :a
  {:text-decoration "none"})

;; `asChild` merges the item's props and ref into its child, which must be a
;; plain element: the anchor is a raw `:a` wearing `menu-link`'s class by name,
;; as an Ornament component is no such element (see `sections/kebab-wrap`).
(defn- action-item
  [[caption on-select icon disabled?]]
  (if (string? on-select)
    ^{:key caption}
    [c/DropdownMenuItem {:asChild true :disabled disabled?}
     [:a {:class  (str menu-link)
          :href   on-select
          :target "_blank"
          :rel    "noopener noreferrer"}
      [dropdown-menu/menu-item-content icon caption]]]
    ^{:key caption}
    [c/DropdownMenuItem {:onSelect on-select :disabled disabled?}
     [dropdown-menu/menu-item-content icon caption]]))

(defn menu
  "`actions` is a vector of `[caption on-select icon disabled?]` — the icon and
   the disabled flag optional. `on-select` is a fn, or a URL: the item is then
   a real link opening it in a new tab, so no popup blocker can stop it and
   it takes a ⌘/middle click like any link. `copy-url` appends a Copy URL
   item putting that URL on the clipboard ([[app-url]] builds one). When
   `on-delete` is given,
   a destructive Delete item is appended that swaps the menu to an inline confirm
   (`confirm-prompt` / `delete-label`) before firing; `delete-disabled-reason`
   disables it instead, with the reason in a tooltip. `aria-label` names the
   trigger for the entity the menu belongs to, and is its tooltip; `icon` is
   drawn on it in place of the ⋮."
  [{:keys [actions copy-url on-delete delete-label confirm-prompt delete-disabled-reason aria-label icon]
    :or {aria-label "Actions"
         icon icons/filled-more_vert}}]
  (r/with-let [!open?    (r/atom false)
               !confirm? (r/atom false)
               close!    (fn [] (reset! !confirm? false) (reset! !open? false))]
    [sections/kebab-wrap
     [c/DropdownMenuRoot
      {:modal false
       :open @!open?
       :onOpenChange (fn [open?]
                       (reset! !open? open?)
                       (when-not open? (reset! !confirm? false)))}
      ;; Named on hover in the house tooltip, as every header button is.
      [tt/tooltip {:content aria-label}
       [c/DropdownMenuTrigger
        [:button {:type "button" :aria-label aria-label}
         [icon]]]]
      ;; Don't return focus to the trigger on close — an "Edit" action moves focus
      ;; into the note's textarea, and Radix's default close-refocus would steal it.
      ;; `onFocusOutside`: the trigger opens on `pointerdown`, and on touch the
      ;; ensuing focus shift bubbles a `focusout` that Radix's non-modal dismiss
      ;; layer reads as an outside interaction, closing the menu the instant it
      ;; opens. Swallow that one — real outside taps still close via the
      ;; pointer/click path, and Escape still closes.
      [c/DropdownMenuContent {:align "end"
                              :onCloseAutoFocus (fn [^js e] (.preventDefault e))
                              :onFocusOutside (fn [^js e] (.preventDefault e))}
       (if @!confirm?
         [:<>
          [confirm-title confirm-prompt]
          [confirm-row
           [confirm-cancel {:type "button" :on-click close!} "Cancel"]
           [confirm-delete {:type "button" :on-click (fn [] (close!) (on-delete))} "Delete"]]]
         (into [:<>]
               (cond-> (mapv action-item actions)
                 copy-url
                 (conj (action-item (copy-url-item copy-url)))

                 on-delete
                 (conj (let [delete-item [c/DropdownMenuItem
                                          {:color "red"
                                           :disabled (some? delete-disabled-reason)
                                           :onSelect (fn [e] (.preventDefault e) (reset! !confirm? true))}
                                          [dropdown-menu/menu-item-content trash-icon (or delete-label "Delete")]]]
                         (if delete-disabled-reason
                           ^{:key "delete"} [c/Tooltip {:content delete-disabled-reason} delete-item]
                           ^{:key "delete"} delete-item))))))]]]))
